<script setup lang="ts">
import { ref } from 'vue';
import { downloadDocumentApi } from '@Api/esignature.api';
import { DocumentDownloadType } from '@Enums/documentDownloadType.enum';

withDefaults(defineProps<{
    showTitle?: boolean;
}>(), {
    showTitle: true,
});

interface DownloadableDocument {
    name: string;
    downloadType: DocumentDownloadType;
}

const documents = ref<DownloadableDocument[]>([
    { name: 'Signed Document', downloadType: DocumentDownloadType.SignedDocumentsPacket },
    { name: 'Certification of Completion', downloadType: DocumentDownloadType.CertificateOfCompletion },
]);

const getFileName = (documentType: DocumentDownloadType): string => {
    return documentType === DocumentDownloadType.SignedDocumentsPacket
        ? 'signed-documents-packet.pdf'
        : documentType === DocumentDownloadType.CertificateOfCompletion
        ? 'certificate-of-completion.pdf'
        : 'signed-documents-with-certificate.pdf';
};

const triggerDownload = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

const downloadDocument = async (document: DownloadableDocument) => {
    try {
        const blob = await downloadDocumentApi(document.downloadType);
        triggerDownload(blob, getFileName(document.downloadType));
    } catch (error) {
        console.error('Failed to download document:', error);
    }
};

const downloadCombinedPdf = async () => {
    try {
        const blob = await downloadDocumentApi(DocumentDownloadType.SignedDocumentsPacketWithCertificate);
        triggerDownload(blob, getFileName(DocumentDownloadType.SignedDocumentsPacketWithCertificate));
    } catch (error) {
        console.error('Failed to download combined PDF:', error);
    }
};
</script>

<template>
    <app-form-content-layout>
        <h1
            v-if="showTitle"
            class="esignature-success__headline">
            Your documents
        </h1>

        <p class="esignature-success__description">
            Thanks for signing. Your executed documents and the certificate
            of completion are ready to download below.
        </p>

        <div class="esignature-success__documents-list">
            <button
                v-for="document in documents"
                :key="document.downloadType"
                type="button"
                class="esignature-success__document-card"
                :data-id="`download-${document.downloadType}`"
                @click.stop.prevent="downloadDocument(document)">
                <div class="esignature-success__document-icon">
                    <app-icon icon="cd-ic-document" />
                </div>
                <p class="esignature-success__document-name">{{ document.name }}</p>
                <app-icon icon="cd-ic-download" class="esignature-success__download-icon" />
            </button>
        </div>

        <button
            type="button"
            class="esignature-success__combined-link"
            data-id="download-combined-pdf"
            @click.stop.prevent="downloadCombinedPdf">
            <span class="esignature-success__combined-link-text">Download combined PDF</span>
            <app-icon icon="cd-ic-download" class="esignature-success__download-icon" />
        </button>
    </app-form-content-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.esignature-success {
    &__headline {
        font-size: 1.625rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-high;
        margin: 0 0 1rem 0;
    }

    &__description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
        margin: 0 0 1.5rem 0;
    }

    &__documents-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin: 0 0 2rem 0;
    }

    &__document-card {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        background: $white;
        border: 0.0625rem solid $dark-gray;
        border-radius: 0.375rem;
        cursor: pointer;
        width: 100%;
        text-align: left;
        height: 4.5rem;

        &:hover {
            box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.1);
        }
    }

    &__document-icon {
        width: 2.5rem;
        height: 2.5rem;
        background: var(--ch-primary-dark);
        border-radius: 3.125rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $white;
        font-size: 1.5rem;
        flex-shrink: 0;
    }

    &__document-name {
        flex: 1;
        margin: 0;
        font-size: 1rem;
        font-weight: 700;
        line-height: 1.5;
        color: $black-high;
    }

    &__download-icon {
        font-size: 1.5rem;
        width: 1.5rem;
        height: 1.5rem;
        color: $theme-primary;
        flex-shrink: 0;
    }

    &__combined-link {
        display: flex;
        align-items: center;
        align-self: flex-start;
        gap: 0.5rem;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;

        &:hover {
            opacity: 0.8;
        }
    }

    &__combined-link-text {
        font-size: 1rem;
        font-weight: 700;
        line-height: 1.5;
        color: $theme-primary;
    }
}
</style>
